import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config';
import { fpsGameState } from './MainScene';
import { EventBus, GameEvent } from '@/utils/EventBus';

/**
 * Phaser scene shell for the first-person 3D rave mode.
 * Lazy-loads Three.js, creates a ThreeScene overlay, delegates rendering.
 * ESC returns to 2D mode. Music keeps playing (you're inside the rave).
 */
export class FPSScene extends Phaser.Scene {
  private threeScene: import('@/fps/ThreeScene').ThreeScene | null = null;
  private hintText: Phaser.GameObjects.Text | null = null;

  constructor() {
    super({ key: 'FPSScene' });
  }

  async create(): Promise<void> {
    // NOTE: music keeps playing — we're walking around inside the rave

    // Show loading text
    const loading = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'Entering the rave...', {
      fontFamily: 'monospace',
      fontSize: '20px',
      color: '#ff00ff',
    });
    loading.setOrigin(0.5, 0.5);
    loading.setDepth(2000);

    // Lazy-load Three.js
    const { ThreeScene } = await import('@/fps/ThreeScene');

    loading.destroy();

    if (!fpsGameState) {
      // No state snapshot available, bail back to 2D
      this.scene.stop('FPSScene');
      this.scene.start('MainScene');
      this.scene.launch('UIScene');
      return;
    }

    this.threeScene = new ThreeScene(fpsGameState);

    // Hint text (rendered by Phaser above the Three.js canvas, but let's put it in Three.js canvas space)
    // Actually, Phaser renders under the Three.js canvas, so we add an HTML hint instead
    const hint = document.createElement('div');
    hint.id = 'fps-hint';
    hint.textContent = 'WASD to move | Mouse to look | Click to capture mouse | ESC to exit';
    hint.style.cssText = `
      position: absolute; bottom: 12px; left: 50%; transform: translateX(-50%);
      font-family: monospace; font-size: 11px; color: #88ff88; opacity: 0.7;
      z-index: 1001; pointer-events: none; text-shadow: 0 0 4px #000;
    `;
    const phaserCanvas = document.querySelector('canvas');
    if (phaserCanvas?.parentElement) {
      phaserCanvas.parentElement.appendChild(hint);
    }

    // ESC handler
    this.input.keyboard!.on('keydown-ESC', () => {
      this.exitFPSMode();
    });
  }

  update(): void {
    if (this.threeScene) {
      this.threeScene.update();
    }
  }

  private exitFPSMode(): void {
    // Dispose Three.js resources
    if (this.threeScene) {
      this.threeScene.dispose();
      this.threeScene = null;
    }

    // Exit pointer lock
    if (document.pointerLockElement) {
      document.exitPointerLock();
    }

    // Remove hint
    const hint = document.getElementById('fps-hint');
    if (hint) hint.remove();

    // MainScene is still running underneath — just tell it FPS mode ended
    EventBus.emit(GameEvent.FPS_MODE_EXITED);
    this.scene.stop('FPSScene');
  }
}
